import React, { memo } from "react"
import {
  Cloud,
  Lock,
  ShoppingCart,
  Save,
  ImportExport,
  MobiledataOff,
} from "@mui/icons-material"
import { Box, Button, CircularProgress, Switch } from "@material-ui/core"
import {
  actionButtonBaseSx,
  detailCardSx,
  detailInfoCardSx,
  detailPillSx,
  modelBadgeSx,
  rightPanelTitleColor,
  themePalette,
} from "./styles"

const SectionTitle = ({ children }) => (
  <h3 style={{ fontSize: "0.78rem", fontWeight: 700, color: rightPanelTitleColor, textTransform: "uppercase", letterSpacing: "0.08em", margin: 0 }}>
    {children}
  </h3>
)

// Resume la informacion principal del medidor seleccionado.
export const SummarySection = memo(function SummarySection({ selectedMeter, summaryChipLabel, showHierarchyChip = true }) {
  return (
    <Box sx={detailCardSx}>
      <Box sx={{ display: "flex", flexDirection: "column", rowGap: "0.5rem" }}>
        <div style={{ fontSize: "0.78rem", fontWeight: 700, color: rightPanelTitleColor, textTransform: "uppercase", letterSpacing: "0.08em" }}>Resumen</div>
        <Box sx={{ display: "flex", flexDirection: "column", rowGap: "0.45rem", paddingTop: "0.2rem", paddingBottom: "0.2rem" }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "0.75rem", flexWrap: "wrap" }}>
            <div style={{ fontSize: "1.05rem", fontWeight: 700, color: themePalette.primary.main, lineHeight: 1.2, marginTop: "0.05rem", marginBottom: "0.2rem" }}>
              {selectedMeter.name || selectedMeter.key || "-"}
            </div>
            <span style={{ ...modelBadgeSx }}>
              {selectedMeter.model}
            </span>
          </Box>
          <div style={{ fontSize: "0.92rem", color: themePalette.text.secondary, lineHeight: 1.35, marginTop: "0.05rem", marginBottom: "0.2rem" }}>
            {selectedMeter.key || "-"}
          </div>
          {showHierarchyChip && (
            <span
              style={{
                ...detailPillSx,
                marginTop: "0.1rem",
                marginBottom: "0.05rem",
                width: "fit-content",
                maxWidth: "100%",
                fontWeight: 500,
                backgroundColor: themePalette.surface.neutral,
                borderColor: themePalette.border.main,
                color: themePalette.text.secondary,
              }}
            >
              {summaryChipLabel}
            </span>
          )}
        </Box>
      </Box>
    </Box>
  )
})

// Edicion del nombre visible solo para dispositivos sin hijos.
export const NameSection = memo(function NameSection({
  editableName,
  setEditableName,
  handleNameBlur,
  loadingSave,
  classes,
}) {
  return (
    <Box sx={detailCardSx}>
      <SectionTitle>Nombre *</SectionTitle>
      <input type="text" value={editableName} onChange={(e) => setEditableName(e.target.value)} style={{ fontSize: "1.05rem", color: themePalette.text.primary, border: `1px solid ${themePalette.border.input}`, borderRadius: "0.3rem", outline: "none", backgroundColor: themePalette.common.white, padding: "0.9rem 1rem" }} />
      <Box sx={{ display: "flex", justifyContent: "flex-end", paddingRight: "1rem" }}>
        <Button onClick={handleNameBlur} disabled={loadingSave} color="primary" variant="contained" className={`${classes.primaryButton} ${classes.saveNameButton}`} style={{ width: "220px", minHeight: "32px", padding: "0.35rem 0.75rem", borderRadius: "0.25rem", fontWeight: 600, fontSize: "0.8rem" }}>
          {loadingSave ? (
            <CircularProgress size={18} style={{ color: themePalette.primary.contrastText }} />
          ) : (
            <>
              <span
                style={{
                  position: "relative",
                  width: "1rem",
                  height: "1rem",
                  marginRight: "0.4rem",
                  marginBottom: "0.1rem",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Save fontSize="small" style={{ position: "absolute" }} />
              </span>
              Guardar Nombre
            </>
          )}
        </Button>
      </Box>
    </Box>
  )
})

// Estado operativo del medidor.
export const StatusSection = memo(function StatusSection({
  selectedMeter,
  handleMeterStatusChange,
  loadingStateChange,
}) {
  return (
    <Box sx={detailCardSx}>
      <SectionTitle>Estado</SectionTitle>
      <Box sx={{ ...detailInfoCardSx, backgroundColor: selectedMeter.active ? themePalette.status.successPanel : themePalette.surface.alt }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "0.9rem" }}>
          <Box sx={{ width: "2.5rem", height: "2.5rem", borderRadius: "0.8rem", display: "inline-flex", alignItems: "center", justifyContent: "center", backgroundColor: selectedMeter.active ? themePalette.status.successSoft : themePalette.surface.neutral }}>
            {selectedMeter.active ? (
              <ImportExport style={{ color: themePalette.status.success }} />
            ) : (
              <MobiledataOff style={{ color: themePalette.text.disabled }} />
            )}
          </Box>
          <Box>
            <div style={{ fontSize: "0.96rem", fontWeight: 700, color: themePalette.text.highlight }}>{selectedMeter.active ? "Dispositivo Activo" : "Dispositivo Inactivo"}</div>
            <div style={{ fontSize: "0.8rem", fontWeight: 700, color: themePalette.text.secondary }}>{selectedMeter.active ? "Envío de datos habilitado." : "Envío de datos deshabilitado."}</div>
            <div style={{ fontSize: "0.8rem", color: themePalette.text.disabled }}>{selectedMeter.active ? "Powermate recibe y almacena los datos del dispositivo." : "Powermate no recibe datos de este dispositivo."}</div>
          </Box>
        </Box>
        <Switch
          checked={selectedMeter.active}
          onChange={() => handleMeterStatusChange(selectedMeter.id)}
          color="primary"
          disabled={loadingStateChange}
        />
      </Box>
    </Box>
  )
})

const getCloudActionConfig = (cloudAccess) => {
  if (cloudAccess === "permanente") {
    return {
      icon: <Cloud fontSize="small" style={{ marginRight: "0.5rem", marginBottom: "0.1rem" }} />,
      label: "Desactivar acceso",
    }
  }

  if (cloudAccess === "trial") {
    return {
      icon: <ShoppingCart fontSize="small" style={{ marginRight: "0.5rem", marginBottom: "0.1rem" }} />,
      label: "Comprar acceso a la nube",
    }
  }

  return {
    icon: <Lock fontSize="small" style={{ marginRight: "0.5rem", marginBottom: "0.1rem" }} />,
    label: "Activar acceso",
  }
}

const getTrialAccessCopy = (selectedCloudSource) => {
  if (selectedCloudSource?.trialExpired) {
    return {
      title: "Prueba expirada",
      description: "El acceso de prueba a la nube expiró.",
    }
  }

  if (typeof selectedCloudSource?.remainingDays === "number") {
    const dayLabel = selectedCloudSource.remainingDays === 1 ? "dia restante" : "dias restantes"
    return {
      title: "Acceso de prueba",
      description: `Quedan ${selectedCloudSource.remainingDays} ${dayLabel} de acceso a la nube.`,
    }
  }

  return {
    title: "Acceso de prueba",
    description: "Acceso temporal de prueba a la nube.",
  }
}

// Estado comercial del acceso a la nube.
export const CloudAccessSection = memo(function CloudAccessSection({
  isMobile,
  selectedMeter,
  selectedCloudSource,
  selectedIsChild,
  shouldShowCloudActionButton,
  selectedCloudButtonDisabled,
  renderCloudAccessBadge,
  handleCloudAccessAction,
  productionUiLock,
  classes,
}) {
  const actionConfig = getCloudActionConfig(selectedCloudSource?.cloudAccess)
  const trialAccessCopy = getTrialAccessCopy(selectedCloudSource)

  return (
    <Box sx={detailCardSx}>
      <SectionTitle>Acceso a la Nube</SectionTitle>
      <Box sx={{ ...detailInfoCardSx, alignItems: isMobile ? "flex-start" : "center", flexDirection: isMobile ? "column" : "row" }}>
        <Box sx={{ display: "flex", flexDirection: "column", rowGap: "0.45rem" }}>
          <div style={{ fontSize: "0.96rem", fontWeight: 700, color: themePalette.text.highlight }}>
            {selectedCloudSource?.cloudAccess === "permanente" && "Acceso permanente"}
            {selectedCloudSource?.cloudAccess === "trial" && trialAccessCopy.title}
            {selectedCloudSource?.cloudAccess === "sin-acceso" && "Sin acceso a la nube"}
          </div>
          <div style={{ fontSize: "0.8rem", color: themePalette.text.disabled }}>
            {selectedCloudSource?.cloudAccess === "permanente" && "Envio de datos a la nube sin restricciones."}
            {selectedCloudSource?.cloudAccess === "trial" && trialAccessCopy.description}
            {selectedCloudSource?.cloudAccess === "sin-acceso" && "No tiene acceso a la nube."}
            {selectedIsChild && " Estado definido por el medidor padre."}
          </div>
          <Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
            {renderCloudAccessBadge(selectedCloudSource)}
          </Box>
        </Box>
        {shouldShowCloudActionButton && (
          <Button
            onClick={() => handleCloudAccessAction(selectedMeter.id, "permanente")}
            className={
              selectedCloudSource?.cloudAccess === "trial"
                ? classes.warningButton
                : selectedCloudSource?.cloudAccess === "sin-acceso"
                  ? classes.outlinedButton
                  : classes.primaryButton
            }
            disabled={selectedCloudButtonDisabled || productionUiLock}
            color={selectedCloudSource?.cloudAccess !== "sin-acceso" ? "primary" : undefined}
            variant={selectedCloudSource?.cloudAccess === "sin-acceso" ? "outlined" : "contained"}
            style={{ ...actionButtonBaseSx, width: isMobile ? "100%" : "260px", paddingRight: "1.25rem", whiteSpace: "nowrap" }}
          >
            {actionConfig.icon}
            <span>{actionConfig.label}</span>
          </Button>
        )}
      </Box>
    </Box>
  )
})

// Zona de acciones destructivas.
export const DangerZoneSection = memo(function DangerZoneSection({
  isMobile,
  openAffirmModal,
  productionUiLock,
  classes,
}) {
  return (
    <Box sx={detailCardSx}>
      <SectionTitle>Acciones</SectionTitle>
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: "center",
          justifyContent: "center",
          columnGap: "1.25rem",
          rowGap: "0.75rem",
          width: "100%",
          padding: "1.4rem 0.9rem",
          borderRadius: "0.3rem",
          border: `1px solid ${themePalette.border.soft}`,
          backgroundColor: themePalette.surface.accentArea,
        }}
      >
        <Button
          fullWidth
          variant="outlined"
          className={classes.deleteDataButton}
          onClick={() => openAffirmModal("delete-data")}
          disabled={productionUiLock}
          style={{
            ...actionButtonBaseSx,
            width: isMobile ? "100%" : actionButtonBaseSx.width,
          }}
        >
          ELIMINAR DATOS
        </Button>
        <Button
          fullWidth
          color="secondary"
          variant="contained"
          className={classes.dangerButton}
          onClick={() => openAffirmModal("delete-device")}
          disabled={productionUiLock}
          style={{
            ...actionButtonBaseSx,
            width: isMobile ? "100%" : actionButtonBaseSx.width,
          }}
        >
          ELIMINAR DISPOSITIVO
        </Button>
      </Box>
    </Box>
  )
})
